// Filterregel-Anwendung für Selektionen.
//
// Eine Filterregel ist ein einfaches JS-Objekt:
// {
//   status: 'alle' | <PRAXIS_STATUS_OPTIONS value>,
//   praxis_art: 'alle' | <PRAXIS_ART_OPTIONS value>,
//   versorgungsform: 'alle' | <VERSORGUNGSFORM_OPTIONS value>,
//   verbund: 'alle' | 'keiner' | <VERBUND_OPTIONS value>,
//   typen: string[]            // leer = alle (Arztpraxis + Klinik); Multi-Select
//   fachgebiete: string[]      // leer = alle, sonst whitelist
//   plz_von: string            // optional, 5-stellige dt. PLZ
//   plz_bis: string            // optional
//   nur_hauptpraxen: boolean   // optional, default false. true → Filialen
//                              // (mutter_praxis gesetzt) werden ausgeschlossen.
// }
//
// Felder dürfen fehlen — eine leere Regel matched alle Praxen. Insbesondere
// verhalten sich bestehende Selektionen ohne `nur_hauptpraxen` weiter wie
// bisher (alles drin, Filialen inklusive).

import { praxisStatusOrDefault, praxisArtOrDefault } from './praxis.js';

function normalisierePlz(value) {
  if (!value) return '';
  return String(value).trim();
}

/**
 * Wendet eine Filterregel auf eine Liste von Praxen an.
 * @param {Array<object>} praxen
 * @param {object | null | undefined} regel
 * @returns {Array<object>}
 */
export function applyFilterRegel(praxen, regel) {
  if (!Array.isArray(praxen)) return [];
  if (!regel || typeof regel !== 'object') return praxen;

  const statusFilter = regel.status && regel.status !== 'alle' ? regel.status : null;
  const praxisArtFilter =
    regel.praxis_art && regel.praxis_art !== 'alle' ? regel.praxis_art : null;
  const versorgungsformFilter =
    regel.versorgungsform && regel.versorgungsform !== 'alle' ? regel.versorgungsform : null;
  const verbundFilter = regel.verbund && regel.verbund !== 'alle' ? regel.verbund : null;
  const typen = Array.isArray(regel.typen) ? regel.typen : [];
  const typenSet = typen.length > 0 ? new Set(typen) : null;
  const fachgebiete = Array.isArray(regel.fachgebiete) ? regel.fachgebiete : [];
  const fachgebieteSet = fachgebiete.length > 0 ? new Set(fachgebiete) : null;
  const plzVon = normalisierePlz(regel.plz_von);
  const plzBis = normalisierePlz(regel.plz_bis);
  const nurHauptpraxen = regel.nur_hauptpraxen === true;

  return praxen.filter((p) => {
    if (nurHauptpraxen && p.mutter_praxis) {
      return false;
    }
    if (statusFilter && praxisStatusOrDefault(p.status) !== statusFilter) {
      return false;
    }
    if (praxisArtFilter && praxisArtOrDefault(p.praxis_art) !== praxisArtFilter) {
      return false;
    }
    // Direkter Vergleich statt OrDefault: bei versorgungsform ist leer kein
    // Synonym für "unbekannt", Praxen ohne Wert fallen also raus.
    if (versorgungsformFilter && p.versorgungsform !== versorgungsformFilter) {
      return false;
    }
    // 'keiner' trifft die Praxen ohne Verbund — der Leerwert ist hier die
    // Aussage "kein Verbund", nicht "nicht erfasst".
    if (verbundFilter === 'keiner') {
      if (p.verbund) return false;
    } else if (verbundFilter && p.verbund !== verbundFilter) {
      return false;
    }
    if (typenSet && !typenSet.has(p.typ)) {
      return false;
    }
    if (fachgebieteSet) {
      // fachgebiet_neu ist der aktuelle, strukturierte Slug.
      // Praxen ohne Wert sind beim Multi-Filter ausgeschlossen.
      if (!p.fachgebiet_neu || !fachgebieteSet.has(p.fachgebiet_neu)) {
        return false;
      }
    }
    if (plzVon || plzBis) {
      const plz = normalisierePlz(p.plz);
      // Praxen ohne PLZ fallen aus dem PLZ-Bereich raus — sonst würde "20000–24999"
      // unter Umständen einen plz-losen Datensatz mitziehen, was die Selektion
      // unbrauchbar macht.
      if (!plz) return false;
      if (plzVon && plz < plzVon) return false;
      if (plzBis && plz > plzBis) return false;
    }
    return true;
  });
}

/**
 * Übersetzt eine Filterregel in eine kurze, lesbare Beschreibung
 * (für die Anzeige im "Filter"-Tab und in der Übersicht).
 */
export function beschreibeFilterRegel(regel, helpers = {}) {
  if (!regel) return [];
  const lines = [];
  const {
    statusLabel,
    praxisArtLabel,
    versorgungsformLabel,
    verbundLabel,
    typLabel,
    fachgebietLabel,
  } = helpers;

  if (regel.status && regel.status !== 'alle') {
    lines.push({
      key: 'status',
      label: 'Status',
      value: statusLabel?.(regel.status) ?? regel.status,
    });
  }
  if (regel.praxis_art && regel.praxis_art !== 'alle') {
    lines.push({
      key: 'praxis_art',
      label: 'Praxis-Art',
      value: praxisArtLabel?.(regel.praxis_art) ?? regel.praxis_art,
    });
  }
  if (regel.versorgungsform && regel.versorgungsform !== 'alle') {
    lines.push({
      key: 'versorgungsform',
      label: 'Versorgungsform',
      value: versorgungsformLabel?.(regel.versorgungsform) ?? regel.versorgungsform,
    });
  }
  if (regel.verbund && regel.verbund !== 'alle') {
    lines.push({
      key: 'verbund',
      label: 'Verbund',
      value: verbundLabel?.(regel.verbund) ?? regel.verbund,
    });
  }
  if (Array.isArray(regel.typen) && regel.typen.length > 0) {
    lines.push({
      key: 'typen',
      label: 'Typ',
      value: regel.typen.map((v) => typLabel?.(v) ?? v).join(', '),
    });
  }
  if (Array.isArray(regel.fachgebiete) && regel.fachgebiete.length > 0) {
    lines.push({
      key: 'fachgebiete',
      label: 'Fachgebiete',
      value: regel.fachgebiete
        .map((slug) => fachgebietLabel?.(slug) ?? slug)
        .join(', '),
    });
  }
  const plzVon = normalisierePlz(regel.plz_von);
  const plzBis = normalisierePlz(regel.plz_bis);
  if (plzVon || plzBis) {
    let value;
    if (plzVon && plzBis) value = `${plzVon} – ${plzBis}`;
    else if (plzVon) value = `ab ${plzVon}`;
    else value = `bis ${plzBis}`;
    lines.push({ key: 'plz', label: 'PLZ', value });
  }
  if (regel.nur_hauptpraxen === true) {
    lines.push({ key: 'nur_hauptpraxen', label: 'Filialen', value: 'ausgeschlossen' });
  }
  return lines;
}
